import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import ColyMascot from "@/components/ColyMascot";
import WaitlistForm from "@/components/WaitlistForm";
import AppBadges from "@/components/AppBadges";
import Welcome from "@/components/Welcome";
import FAQ from "@/components/FAQ";
import Navbar from "@/components/Navbar";
import FeatureSuggest from "@/components/FeatureSuggest";
import Wordmark from "@/components/Wordmark";
import { PhoneFrame, ScreenshotImage, SCREENSHOTS } from "@/components/PhoneMockup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const [count, setCount] = useState(null);

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/waitlist/count`);
      setCount(res.data.count);
    } catch (e) {}
  };

  useEffect(() => {
    fetchCount();
  }, []);

  return (
    <div className="min-h-screen bg-coly-cream text-coly-ink overflow-x-hidden">
      <Navbar />
      <Hero onJoined={fetchCount} />
      <Welcome />
      <MascotSection />
      <FAQ />
      <FeatureSuggest />
      <FinalCta onJoined={fetchCount} />
    </div>
  );
};

/* ---------------- HERO ---------------- */

const Hero = ({ onJoined }) => (
  <section className="relative overflow-hidden pt-10 sm:pt-16 pb-12 sm:pb-20" data-testid="hero">
    {/* blurred radial blobs */}
    <div
      className="absolute -top-[120px] -left-[160px] w-[460px] h-[460px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(111,176,137,0.22), transparent 60%)", filter: "blur(20px)" }}
    />
    <div
      className="absolute top-20 -right-[100px] w-[380px] h-[380px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(244,162,140,0.18), transparent 60%)", filter: "blur(18px)" }}
    />

    <div
      className="relative max-w-[1180px] mx-auto px-5 sm:px-6 grid items-center gap-10 lg:gap-14 lg:grid-cols-[1.05fr_1fr]"
    >
      {/* LEFT — copy & form */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-center lg:text-left"
      >
        <h1
          className="text-[36px] sm:text-[48px] md:text-[56px] lg:text-[68px] font-black m-0 leading-[1.02] tracking-[-1.5px] sm:tracking-[-2px]"
          style={{ textWrap: "balance" }}
        >
          Living with IBD diseases,<br />
          just got a little<br />
          <span className="relative inline-block">
            <span className="relative z-10 text-coly-green-deep">friendlier.</span>
            <svg
              width="100%"
              height="18"
              viewBox="0 0 320 18"
              preserveAspectRatio="none"
              className="absolute left-0 -bottom-0.5 z-0"
            >
              <motion.path
                d="M4 12 Q 80 2, 160 9 T 316 8"
                stroke="#F4C95D"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                opacity="0.7"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.9, delay: 0.5, ease: "easeOut" }}
              />
            </svg>
          </span>
        </h1>

        <p className="text-[17px] sm:text-[19px] leading-[1.5] text-coly-ink-soft max-w-[520px] mx-auto lg:mx-0 my-5 sm:my-6 font-medium">
          Track symptoms, meds and flares in seconds — and let Coly turn the boring stuff into tiny daily wins.
        </p>

        <div id="waitlist" className="mb-5 flex justify-center lg:justify-start">
          <WaitlistForm onJoined={onJoined} />
        </div>

        <div className="mb-7 flex justify-center lg:justify-start">
          <AppBadges />
        </div>
      </motion.div>

      {/* RIGHT — single phone on tablet, stack on desktop */}
      <div className="relative h-[420px] sm:h-[560px] lg:h-[680px] flex items-center justify-center lg:block">
        <div className="absolute w-[280px] sm:w-[420px] lg:w-[460px] h-[280px] sm:h-[420px] lg:h-[460px] rounded-full bg-coly-green/10 lg:-top-2 lg:left-2" />
        <div className="absolute w-[220px] sm:w-[340px] lg:w-[380px] h-[220px] sm:h-[340px] lg:h-[380px] rounded-full bg-coly-yellow/15 top-2 right-0 lg:top-8" />

        {/* Insights phone — only on desktop (peek behind) */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: -12 }}
          animate={{ opacity: 1, y: 0, rotate: -8 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="hidden lg:block absolute -left-5 bottom-16 z-[1]"
        >
          <PhoneFrame scale={0.48} rotate={-8} bare>
            <ScreenshotImage src={SCREENSHOTS.fullCheckin} alt="Coly full check-in" />
          </PhoneFrame>
        </motion.div>

        {/* Home phone — always visible, scales by breakpoint */}
        <motion.div
          initial={{ opacity: 0, y: 30, rotate: 8 }}
          animate={{ opacity: 1, y: 0, rotate: 3 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative z-[2] flex justify-center lg:justify-center"
        >
          <div className="block sm:hidden">
            <PhoneFrame scale={0.48} rotate={3} bare>
              <ScreenshotImage src={SCREENSHOTS.diary} alt="Coly diary" />
            </PhoneFrame>
          </div>
          <div className="hidden sm:block lg:hidden">
            <PhoneFrame scale={0.58} rotate={3} bare>
              <ScreenshotImage src={SCREENSHOTS.diary} alt="Coly diary" />
            </PhoneFrame>
          </div>
          <div className="hidden lg:block">
            <PhoneFrame scale={0.68} rotate={3} bare>
              <ScreenshotImage src={SCREENSHOTS.diary} alt="Coly diary" />
            </PhoneFrame>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------------- MASCOT SECTION ---------------- */

const MOODS = [
  { k: "good", label: "Good day", emoji: "🌞" },
  { k: "quiet", label: "Quiet day", emoji: "🌿" },
  { k: "meh", label: "Meh day", emoji: "😐" },
  { k: "tough", label: "Tough day", emoji: "🫂" },
  { k: "flare", label: "Flare", emoji: "🔥" },
  { k: "streak", label: "Streak!", emoji: "✨" },
  { k: "resting", label: "Resting", emoji: "💤" },
];

const MascotSection = () => {
  const [mood, setMood] = useState("good");
  return (
    <section id="mascot" data-testid="mascot-section" className="py-16 sm:py-24 relative">
      <div className="max-w-[1180px] mx-auto px-5 sm:px-6 grid items-center gap-10 sm:gap-16 md:grid-cols-2">
        {/* big mascot card */}
        <div
          className="relative rounded-[28px] sm:rounded-[36px] p-6 sm:p-10 overflow-hidden border border-coly-ink/10 min-h-[260px] sm:min-h-[360px] flex items-center justify-center"
          style={{ background: "#F3EEE2" }}
        >
          {/* dotted bg */}
          <svg width="520" height="380" className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 22 }).map((_, i) => {
              const seed = (i * 9301 + 49297) % 233280;
              const cx = (seed / 233280) * 520;
              const cy = ((seed * 1.7) % 380);
              const r = ((seed * 0.013) % 3) + 1.5;
              return <circle key={i} cx={cx} cy={cy} r={r} fill="#6FB089" opacity="0.25" />;
            })}
          </svg>
          <div className="absolute w-[210px] h-[210px] sm:w-[260px] sm:h-[260px] rounded-full bg-coly-green/15" />
          <div
            className="relative"
            style={{ filter: "drop-shadow(0 18px 30px rgba(47,58,44,0.18))" }}
          >
            <ColyMascot size={170} className="sm:hidden" mood={mood} withSparkles animate />
            <div className="hidden sm:block">
              <ColyMascot size={220} mood={mood} withSparkles animate />
            </div>
          </div>
        </div>

        <div>
          <div className="inline-block text-[11px] font-extrabold tracking-[1.6px] text-coly-yellow-deep uppercase mb-3.5">
            Meet Coly
          </div>
          <h2 className="text-[32px] sm:text-[40px] lg:text-[46px] font-black m-0 tracking-[-1px] sm:tracking-[-1.2px] leading-[1.05]">
            Your gut's<br />tiny best friend.
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.55] text-coly-ink-soft my-5 max-w-[460px] font-medium">
            Coly reflects how you're feeling. Cheers when you log, naps when you rest, and never once
            tells you to "just relax." Tap a day to see what they look like.
          </p>
          <div className="flex flex-wrap gap-2.5" data-testid="mood-pills">
            {MOODS.map((m, i) => {
              const active = mood === m.k;
              return (
                <motion.button
                  key={m.k}
                  data-testid={`mood-${m.k}`}
                  onClick={() => setMood(m.k)}
                  whileTap={{ scale: 0.94 }}
                  whileHover={{ y: -3, rotate: i % 2 ? -1.5 : 1.5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 18 }}
                  className={`px-4 py-2.5 rounded-full font-extrabold text-[13px] inline-flex items-center gap-1.5 transition-colors ${
                    active
                      ? "bg-coly-green text-white"
                      : "bg-white text-coly-ink border-2 border-coly-ink/10 hover:border-coly-green"
                  }`}
                  style={
                    active
                      ? { boxShadow: "0 4px 0 #4F8E6A, 0 6px 14px rgba(111,176,137,0.35)" }
                      : { boxShadow: "0 2px 0 rgba(47,58,44,0.08)" }
                  }
                >
                  <span className="text-base leading-none">{m.emoji}</span>
                  {m.label}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- FINAL CTA ---------------- */

const FinalCta = ({ onJoined }) => (
  <section className="px-6 pt-16 pb-24" data-testid="cta-strip">
    <div className="max-w-[1180px] mx-auto">
      <div
        className="relative bg-coly-green text-white rounded-[36px] p-10 sm:p-16 overflow-hidden"
        style={{ boxShadow: "0 8px 0 #4F8E6A, 0 24px 60px rgba(47,58,44,0.18)" }}
      >
        {/* decorative circles */}
        <div className="absolute -top-[120px] -right-[80px] w-[360px] h-[360px] rounded-full bg-white/10" />
        <div className="absolute -bottom-[80px] right-[200px] w-[200px] h-[200px] rounded-full bg-white/[0.08]" />

        {/* mascot peeking */}
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ type: "spring", stiffness: 60, damping: 14, delay: 0.2 }}
          className="absolute right-4 -bottom-2 sm:right-12 hidden sm:block"
          style={{ filter: "drop-shadow(0 12px 20px rgba(47,58,44,0.2))" }}
          data-testid="cta-mascot"
        >
          <ColyMascot size={170} bodyColor="#fff" mood="good" withSparkles className="-rotate-[8deg] lg:scale-110" />
        </motion.div>

        <div className="relative max-w-[580px]">
          <h2 className="text-[34px] sm:text-[42px] lg:text-[50px] font-black m-0 tracking-[-1.2px] sm:tracking-[-1.4px] leading-[1.02]">
            Be the first<br />to log a good day.
          </h2>
          <p className="text-[15px] sm:text-[17px] leading-[1.5] opacity-90 font-medium my-4 sm:my-5 max-w-[460px]">
            Drop your email — we'll send one (1) friendly note when Coly is ready, and nothing else.
          </p>
          <WaitlistForm idSuffix="-cta" onJoined={onJoined} />
          <div className="mt-5 sm:mt-6">
            <AppBadges />
          </div>

          {/* mobile-only mascot under content */}
          <div className="flex justify-center mt-8 sm:hidden">
            <ColyMascot size={140} bodyColor="#fff" mood="good" withSparkles className="-rotate-[6deg]" />
          </div>
        </div>
      </div>

      <footer
        className="mt-8 sm:mt-10 flex flex-col sm:flex-row justify-between items-center gap-4 text-[12px] text-coly-ink-soft px-2"
        data-testid="footer"
      >
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <ColyMascot size={28} mood="good" withSparkles={false} animate={false} />
          <Wordmark height={22} color="#1E2F23" />
          <span>· Made with 💚 for gut warriors</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 justify-center font-semibold text-[11px]">
          <a href="/support" className="hover:text-coly-green-deep transition-colors">Contact</a>
          <a href="/legal/privacy" className="hover:text-coly-green-deep transition-colors">Privacy</a>
          <a href="/legal/terms" className="hover:text-coly-green-deep transition-colors">Terms</a>
          <a href="/legal/cookies" className="hover:text-coly-green-deep transition-colors">Cookies</a>
          <a href="/legal/waitlist-privacy" className="hover:text-coly-green-deep transition-colors">Waitlist notice</a>
          <a href="/legal/app-privacy" className="hover:text-coly-green-deep transition-colors">App Privacy</a>
          <a href="/legal/app-terms" className="hover:text-coly-green-deep transition-colors">App Terms</a>
          <a href="mailto:support@colyapp.com" className="hover:text-coly-green-deep transition-colors">Email</a>
        </div>
        <p className="font-semibold text-center sm:text-right text-[11px]">
          © {new Date().getFullYear()} Coly. Wellness tracking app, not a medical device. Always consult your doctor.
        </p>
      </footer>
    </div>
  </section>
);

export default Landing;
